import React from 'react';
import styles from './CadInfractionsInformation.module.scss';
import {
  CadAddressValueType,
  CadChargeValueType,
  CadOffenseValueType,
  CadPersonValueType,
  CadPremiseValueType,
  CadSubjectValueType,
  CadInfractionValueType
} from '../../../../../Models/CadReportValue';
import {
  FieldItemList,
  FieldItemListType
} from '../../CommonBody/FieldItemList/FieldItemList';
import {
  getCADAddressFull,
  getCADPersonnelFullName,
  getCADPremiseName,
  getCADSubjectFullName
} from '../../CommonBody/Helpers';

const getChargeFields = (
  charge: CadChargeValueType,
  offenses: CadOffenseValueType[],
  personnel: CadPersonValueType[]
): FieldItemListType[] => {
  const offense = offenses.find((offense) => offense.id === charge.offense_id);
  const result: FieldItemListType[] = [];

  if (offense?.id || charge.offense_id) {
    result.push({
      label: 'Offense ID',
      value: offense?.id || charge.offense_id,
      type: 'string'
    });
  }

  if (offense?.code) {
    result.push({
      label: 'Code',
      value: offense?.code,
      type: 'string'
    });
  }

  if (offense?.code_description) {
    result.push({
      label: 'Code Description',
      value: offense?.code_description,
      type: 'string'
    });
  }

  if (offense?.ibr_code) {
    result.push({
      label: 'IBR Code',
      value: offense?.ibr_code,
      type: 'string'
    });
  }

  if (offense?.ucr_code) {
    result.push({
      label: 'UCR Code',
      value: offense?.ucr_code,
      type: 'string'
    });
  }

  if (offense?.officer_assigned_badge_id) {
    result.push({
      label: 'Officer Assigned to the Case',
      value: getCADPersonnelFullName(
        offense?.officer_assigned_badge_id as string,
        personnel as CadPersonValueType[]
      ),
      type: 'string'
    });
  }

  if (offense?.status) {
    result.push({
      label: 'Status',
      value: offense?.status,
      type: 'string'
    });
  }

  if (charge.type) {
    result.push({
      label: 'Type',
      value: charge.type,
      type: 'string'
    });
  }

  return result;
};

const getInfractionFields = (
  infraction: CadInfractionValueType,
  premises: CadPremiseValueType[],
  addresses: CadAddressValueType[],
  personnel: CadPersonValueType[],
  subjects: CadSubjectValueType[],
  offenses: CadOffenseValueType[]
): FieldItemListType[] => {
  let infractionFields: FieldItemListType[] = [];

  if (infraction.case_number) {
    infractionFields.push({
      label: 'Case Number',
      value: infraction.case_number,
      type: 'string'
    });
  }

  if (infraction.agency) {
    infractionFields.push({
      label: 'Agency',
      value: infraction.agency,
      type: 'string'
    });
  }

  if (infraction.subject_id && subjects.length > 0) {
    infractionFields.push({
      label: 'Subject',
      value: getCADSubjectFullName(infraction.subject_id, subjects),
      type: 'string'
    });
  }

  if (infraction.type) {
    infractionFields.push({
      label: 'Type',
      value: infraction.type,
      type: 'string'
    });
  }

  if (infraction.date_time) {
    infractionFields.push({
      label: 'Date and Time',
      value: infraction.date_time,
      type: 'datetime'
    });
  }

  if (infraction.premise_id) {
    infractionFields.push({
      label: 'Premise',
      value: getCADPremiseName(infraction.premise_id, premises),
      type: 'string'
    });
  }

  if (infraction.address_id) {
    infractionFields.push({
      label: 'Address',
      value: getCADAddressFull(
        infraction.address_id,
        addresses as CadAddressValueType[]
      ),
      type: 'string'
    });
  }

  if (infraction.officer_badge_id) {
    infractionFields.push({
      label: 'Officer',
      value: getCADPersonnelFullName(
        infraction.officer_badge_id,
        personnel as CadPersonValueType[]
      ),
      type: 'string'
    });
  }

  if (infraction.charges && infraction.charges.length > 0) {
    infractionFields.push({
      label: 'Charges',
      value: infraction.charges.map((charge) => {
        return getChargeFields(charge, offenses, personnel);
      }),
      type: 'subList'
    });
  }

  return infractionFields;
};

export interface CadInfractionsInformationProps {
  title: string;
  infractions?: CadInfractionValueType[];
  premises?: CadPremiseValueType[];
  addresses?: CadAddressValueType[];
  personnel?: CadPersonValueType[];
  subjects?: CadSubjectValueType[];
  offenses?: CadOffenseValueType[];
}

export const CadInfractionsInformation = ({
  title,
  infractions,
  premises,
  addresses,
  personnel,
  subjects,
  offenses
}: CadInfractionsInformationProps): React.ReactElement<CadInfractionsInformationProps> => {
  return (
    <div className={styles.cadInfractionsInformation}>
      <h3 className={styles.title}>{title}</h3>
      <div className={styles.items}>
        {infractions?.map((infraction, index) => (
          <FieldItemList
            key={infraction.id}
            title={`${index === 0 ? infraction.number : ''}`}
            fields={getInfractionFields(
              infraction as CadInfractionValueType,
              premises as CadPremiseValueType[],
              addresses as CadAddressValueType[],
              personnel as CadPersonValueType[],
              subjects as CadSubjectValueType[],
              offenses as CadOffenseValueType[]
            )}
            hasDivider={index < infractions.length - 1}
          />
        ))}
      </div>
    </div>
  );
};
